import { useState } from 'react';
import { useDiscardUnsyncedDrafts } from '../hooks/useDraftOutbox';
import { useRepositories } from '../hooks/useRepositories';
import { ActionError } from './PageStates';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

/**
 * Deleting my account (019 R21): nothing is sent before I confirm. The
 * server ends the session, so success is a whole-page load of sign-in; a
 * failure leaves me signed in, with the reason, free to try again.
 */
export default function DeleteAccountDialog({
  open,
  onOpenChange,
  assign = (url: string) => window.location.assign(url),
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The full-page navigation; injected by tests. */
  assign?: (url: string) => void;
}) {
  const { account } = useRepositories();
  const discardUnsyncedDrafts = useDiscardUnsyncedDrafts();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await account.deleteAccount();
      // Gone with the account, and no leave-page warning on the way out (021 R10).
      discardUnsyncedDrafts();
      assign('/accounts/login/');
    } catch (e) {
      setError(e);
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="rounded-3xl border-none bg-background">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete your account?</AlertDialogTitle>
          <AlertDialogDescription>
            This can't be undone. Your collections and their listings will be deleted.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error !== null && <ActionError error={error} />}
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full" disabled={busy}>
            Cancel
          </AlertDialogCancel>
          <Button variant="destructive" className="rounded-full" disabled={busy} onClick={confirm}>
            Delete account
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
